import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, Button, Paper, TextField, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, Alert, CircularProgress, Divider,
  Tooltip, Chip, Autocomplete, Tabs, Tab, Checkbox,
  InputAdornment, Collapse
} from '@mui/material';
import { Add, Delete, Calculate, CheckBox, CheckBoxOutlineBlank, TuneOutlined, Save, ArrowBack } from '@mui/icons-material';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Customer, Product, MarketParams, CustomerContact } from '../../types';
import { getCustomers, getContacts } from '../../api/customers';
import { getProducts } from '../../api/products';
import { createOffer } from '../../api/offers';
import { getMarketParams } from '../../api/marketParams';
import { getPriceList, PriceListItem } from '../../api/priceList';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';

// ─── Tasarım sabitleri ─────────────────────────────────────────────────────────
const BRAND  = '#1A3A6B';
const ACCENT = '#c0392b';
const BORDER = '#e8eaf0';

// ─── Types ────────────────────────────────────────────────────────────────────
interface ItemRow {
  productId: number; productName: string; unit: string;
  unitWeight: number; alFactor: number; costPrice: number;
  quantity: number; unitPrice: number; profitMargin: number;
  total: number; autoPrice: number; autoPriceUsd: number; adet: number;
}
interface PriceListItemRow {
  priceListId: number; name: string; unit: string; cableType: string;
  basePriceTl: number | null; basePriceUsd: number | null;
  discount: number; adet: number; quantity: number;
  unitPrice: number; total: number;
}
interface OfferParams {
  lmeAl: number; usdTry: number; eurTry: number;
  finans: number; nakliye: number; diger: number; vergiKad: number;
}

// ─── Yardımcı: bölüm başlığı ─────────────────────────────────────────────────
const SectionTitle: React.FC<{ color?: string; children: React.ReactNode }> = ({ color = BRAND, children }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      <Box sx={{ width: 4, height: 20, bgcolor: color, borderRadius: 1, flexShrink: 0 }} />
      <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#1a2535' }}>{children}</Typography>
    </Box>
);

// ─── Tablo başlık hücresi ──────────────────────────────────────────────────────
const TH: React.FC<{ children?: React.ReactNode; align?: 'left' | 'right' | 'center'; width?: number | string; minWidth?: number }> = ({ children, align = 'left', width, minWidth }) => (
    <TableCell align={align} style={{
      background: '#f8f9fc', color: '#8a92a6',
      fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
      borderBottom: `1px solid ${BORDER}`, paddingTop: 12, paddingBottom: 12,
      width, minWidth,
    }}>
      {children}
    </TableCell>
);

const NewOfferPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [marketParams, setMarketParams] = useState<MarketParams | null>(null);
  const [priceListItems, setPriceListItems] = useState<PriceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showParamsEdit, setShowParamsEdit] = useState(false);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    customerId: '', deliveryLocation: 'EXW', downPaymentRate: 30,
    paymentDays: 0, currency: 'USD', notes: '', language: 'EN',
  });

  const offerParamsRef = useRef<OfferParams>({ lmeAl:0, usdTry:0, eurTry:0, finans:0, nakliye:0, diger:0, vergiKad:0 });
  const [offerParams, setOfferParams] = useState<OfferParams>({
    lmeAl: 0, usdTry: 0, eurTry: 0, finans: 0, nakliye: 0, diger: 0, vergiKad: 0,
  });

  const [items, setItems] = useState<ItemRow[]>([]);
  const [competitorPrices, setCompetitorPrices] = useState<any[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [plItems, setPlItems] = useState<PriceListItemRow[]>([]);
  const [selectedPlItems, setSelectedPlItems] = useState<PriceListItem[]>([]);
  const [globalDiscount, setGlobalDiscount] = useState(0);
  const [termsForm, setTermsForm] = useState({ paymentTerms: '', deliveryTime: '', deliveryPlace: '', optionTerms: '' });
  const [nakliyeMode, setNakliyeMode] = useState<'percent' | 'amount'>('percent');
  const [nakliyeAmount, setNakliyeAmount] = useState(0);
  const [prevCurrency, setPrevCurrency] = useState('USD');
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<string>('');

  useEffect(() => {
    Promise.all([getCustomers(), getProducts(), getMarketParams(), getPriceList(), api.get('/competitor-prices').then(r => r.data).catch(() => [])])
        .then(([c, p, m, pl, cp]: any[]) => {
          setCompetitorPrices(cp); setCustomers(c); setProducts(p);
          setMarketParams(m); setPriceListItems(pl);
          const initParams = { lmeAl: m.lmeAl||0, usdTry: m.usdTry||0, eurTry: m.eurTry||0, finans: m.finans||0, nakliye: m.nakliye||0, diger: m.diger||0, vergiKad: m.vergiKad||0 };
          offerParamsRef.current = initParams; setOfferParams(initParams);
        })
        .catch(() => setError('Veriler yüklenemedi'))
        .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!form.customerId) { setContacts([]); setSelectedContactId(''); return; }
    getContacts(parseInt(form.customerId))
        .then(list => { setContacts(list); setSelectedContactId(list.length > 0 ? String(list[0].id) : ''); })
        .catch(() => { setContacts([]); setSelectedContactId(''); });
  }, [form.customerId]); // eslint-disable-line

  const alPrim  = marketParams?.alPrim || 0;
  const alasim  = offerParams.lmeAl + alPrim + 650;

  const convertFromUsd = (priceUsd: number, currency: string, params: typeof offerParams): number => {
    if (currency === 'TRY') return parseFloat((priceUsd * params.usdTry).toFixed(2));
    if (currency === 'EUR') return parseFloat((priceUsd / (params.eurTry / params.usdTry)).toFixed(3));
    return parseFloat(priceUsd.toFixed(3));
  };

  const calcAutoPrice = (product: Product, currency: string, params?: typeof offerParams): number => {
    const p = params || offerParamsRef.current;
    const vergiOrani = p.vergiKad === 0 ? 0.0 : 0.075;
    const currentAlasim = (p.lmeAl + alPrim) * (1 + vergiOrani) + 650;
    const extras = 1 + p.finans / 100 + p.nakliye / 100 + p.diger / 100;
    let priceUsd: number;
    if (product.cableFamily === 'Compacted' || product.cableFamily === 'AAC-ACSR') {
      priceUsd = (product.costPrice || 0) * extras;
    } else if (product.unit === 'KG') {
      priceUsd = (currentAlasim * (product.aluminumFactor || 0) * extras) / 1000 + (product.costPrice || 0);
    } else {
      const alFactor = product.aluminumFactor || 0;
      const baseVergi = (marketParams?.vergiKad || 0) === 0 ? 0.0 : 0.075;
      const baseLmeWithVergi = ((marketParams?.lmeAl || 0) + (marketParams?.alPrim || 0)) * (1 + baseVergi) + 650;
      const baseExtras = 1 + (marketParams?.finans || 0) / 100 + (marketParams?.nakliye || 0) / 100 + (marketParams?.diger || 0) / 100;
      const alCostBase = (baseLmeWithVergi * alFactor * baseExtras) / 1000;
      const digerCost = (product.costPrice || 0) - alCostBase;
      const alCostNew = (currentAlasim * alFactor * extras) / 1000;
      priceUsd = alCostNew + digerCost;
    }
    const eurUsd = p.eurTry / p.usdTry;
    if (currency === 'EUR') return parseFloat((priceUsd / eurUsd).toFixed(3));
    if (currency === 'TRY') return parseFloat((priceUsd * p.usdTry).toFixed(2));
    return parseFloat(priceUsd.toFixed(3));
  };

  useEffect(() => {
    if (items.length === 0) return;
    const pp = offerParamsRef.current;
    const extMult = 1 + (pp.finans + pp.nakliye + pp.diger) / 100;
    setItems(prev => prev.map(item => {
      const autoPrice = item.autoPriceUsd > 0
        ? convertFromUsd(item.autoPriceUsd * extMult, form.currency, pp)
        : (() => { const product = products.find(p => p.id === item.productId); return product ? calcAutoPrice(product, form.currency) : item.autoPrice; })();
      const unitPrice = parseFloat((autoPrice * (1 + item.profitMargin / 100)).toFixed(3));
      return { ...item, autoPrice, unitPrice, total: item.adet * item.quantity * unitPrice };
    }));
    if (nakliyeMode === 'amount' && nakliyeAmount > 0 && prevCurrency !== form.currency) {
      const pp2 = offerParamsRef.current;
      const toUsd = (p: number, c: string) => c === 'TRY' ? p / pp2.usdTry : c === 'EUR' ? p * (pp2.eurTry / pp2.usdTry) : p;
      const fromUsd = (p: number, c: string) => c === 'TRY' ? p * pp2.usdTry : c === 'EUR' ? p / (pp2.eurTry / pp2.usdTry) : p;
      setNakliyeAmount(parseFloat(fromUsd(toUsd(nakliyeAmount, prevCurrency), form.currency).toFixed(2)));
    }
    setPrevCurrency(form.currency);
  }, [form.currency]); // eslint-disable-line

  useEffect(() => {
    if (plItems.length === 0) return;
    setPlItems(prev => prev.map(item => {
      const plItem = priceListItems.find(p => p.id === item.priceListId);
      if (!plItem) return item;
      const unitPrice = calcPlUnitPrice(plItem, item.discount, form.currency);
      return { ...item, unitPrice, total: item.adet * item.quantity * unitPrice };
    }));
  }, [form.currency]); // eslint-disable-line

  const addItems = async () => {
    if (selectedProducts.length === 0) return;
    const p = offerParamsRef.current;
    const productIds = selectedProducts.map(pr => pr.id);
    let priceMap = new Map<number, number>();
    try {
      const response = await api.post('/cost/calculate-offer-prices', {
        productIds, lmeAl: p.lmeAl, usdTry: p.usdTry, eurTry: p.eurTry,
        finans: p.finans, nakliye: p.nakliye, diger: p.diger, vergiKad: p.vergiKad,
      });
      response.data.results.forEach((r: any) => priceMap.set(r.productId, r.autoPrice));
    } catch (_) { /* fallback to calcAutoPrice below */ }

    const extMult = 1 + (p.finans + p.nakliye + p.diger) / 100;
    const newRows: ItemRow[] = [];
    selectedProducts.forEach(product => {
      const qty = product.unit === 'KG' ? 1000 : 500;
      const autoPriceUsd = priceMap.get(product.id) ?? 0;
      const autoPrice = autoPriceUsd > 0
        ? convertFromUsd(autoPriceUsd * extMult, form.currency, p)
        : calcAutoPrice(product, form.currency);
      const unitPrice = parseFloat((autoPrice * (1 + 0.10)).toFixed(3));
      newRows.push({ productId: product.id, productName: product.name, unit: product.unit, unitWeight: product.unitWeight || 0, alFactor: product.aluminumFactor || 0, costPrice: autoPriceUsd, quantity: qty, unitPrice, profitMargin: 10, total: qty * unitPrice, autoPrice, autoPriceUsd, adet: 1 });
    });
    setItems(prev => [...prev, ...newRows]); setSelectedProducts([]);
  };

  const recalcAll = async (params?: typeof offerParams) => {
    if (items.length === 0) return;
    const currentParams = params || offerParamsRef.current;
    const productIds = Array.from(new Set(items.map(item => item.productId)));
    let priceMap = new Map<number, number>();
    try {
      const response = await api.post('/cost/calculate-offer-prices', {
        productIds, lmeAl: currentParams.lmeAl, usdTry: currentParams.usdTry, eurTry: currentParams.eurTry,
        finans: currentParams.finans, nakliye: currentParams.nakliye, diger: currentParams.diger, vergiKad: currentParams.vergiKad,
      });
      response.data.results.forEach((r: any) => priceMap.set(r.productId, r.autoPrice));
    } catch (_) { /* fallback to calcAutoPrice below */ }

    const extMult = 1 + (currentParams.finans + currentParams.nakliye + currentParams.diger) / 100;
    setItems(prev => prev.map(item => {
      const autoPriceUsd = priceMap.get(item.productId) ?? item.autoPriceUsd;
      const autoPrice = autoPriceUsd > 0
        ? convertFromUsd(autoPriceUsd * extMult, form.currency, currentParams)
        : (() => { const product = products.find(p => p.id === item.productId); return product ? calcAutoPrice(product, form.currency, currentParams) : item.autoPrice; })();
      const unitPrice = parseFloat((autoPrice * (1 + item.profitMargin / 100)).toFixed(3));
      return { ...item, autoPriceUsd: autoPriceUsd || item.autoPriceUsd, autoPrice, unitPrice, total: item.adet * item.quantity * unitPrice };
    }));
  };

  const updateItem = (idx: number, field: keyof ItemRow, value: number) => {
    setItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      if (field === 'profitMargin') {
        updated.unitPrice = parseFloat((item.autoPrice * (1 + value / 100)).toFixed(3));
      } else if (field === 'unitPrice' && item.autoPrice > 0) {
        updated.profitMargin = parseFloat(((value / item.autoPrice - 1) * 100).toFixed(1));
      }
      updated.total = updated.adet * updated.quantity * updated.unitPrice;
      return updated;
    }));
  };

  const removeItem = (idx: number) => setItems(prev => prev.filter((_, i) => i !== idx));

  const getCompetitorComparison = (item: ItemRow) => {
    const specMatch = item.productName.match(/(\d+x\d+)/);
    if (!specMatch) return null;
    const spec = specMatch[1];
    const familyMatch = item.productName.match(/^([A-Z0-9]+)/);
    if (!familyMatch) return null;
    const family = familyMatch[1];
    const matches = competitorPrices.filter((cp: any) => cp.cableFamily === family && cp.spec === spec && cp.competitorName !== 'KLC');
    if (matches.length === 0) return null;
    const usdTry = offerParams.usdTry || 0;
    const eurTry = offerParams.eurTry || 0;
    let bizTl = item.unitPrice;
    if (form.currency === 'USD') bizTl = item.unitPrice * usdTry;
    else if (form.currency === 'EUR') bizTl = item.unitPrice * eurTry;
    return matches.map((m: any): any => {
      if (!m.listPriceTl || bizTl <= 0) return { name: m.competitorName, status: 'no-data' };
      if (bizTl > m.listPriceTl) { const overPct = ((bizTl - m.listPriceTl) / m.listPriceTl) * 100; return { name: m.competitorName, status: 'more-expensive', listPrice: m.listPriceTl, overPct: overPct.toFixed(1) }; }
      const requiredDiscount = ((m.listPriceTl - bizTl) / m.listPriceTl) * 100;
      return { name: m.competitorName, status: 'need-discount', listPrice: m.listPriceTl, requiredDiscount: requiredDiscount.toFixed(1) };
    });
  };

  const allCompetitors = competitorPrices.filter((cp: any) => cp.competitorName !== 'KLC').map((cp: any) => cp.competitorName).filter((v: string, i: number, a: string[]) => a.indexOf(v) === i);

  const getPlCompetitorComparison = (item: PriceListItemRow) => {
    const specMatch = item.name.match(/(\d+x\d+)/);
    if (!specMatch) return null;
    const spec = specMatch[1];
    const familyMatch = item.name.match(/^([A-Z0-9]+)/);
    if (!familyMatch) return null;
    const family = familyMatch[1];
    const matches = competitorPrices.filter((cp: any) => cp.cableFamily === family && cp.spec === spec && cp.competitorName !== 'KLC');
    if (matches.length === 0) return null;
    const usdTry = offerParams.usdTry || 0; const eurTry = offerParams.eurTry || 0;
    let bizTl = item.unitPrice;
    if (form.currency === 'USD') bizTl = item.unitPrice * usdTry;
    else if (form.currency === 'EUR') bizTl = item.unitPrice * eurTry;
    return matches.map((m: any): any => {
      if (!m.listPriceTl || bizTl <= 0) return { name: m.competitorName, status: 'no-data' };
      if (bizTl > m.listPriceTl) { const overPct = ((bizTl - m.listPriceTl) / m.listPriceTl) * 100; return { name: m.competitorName, status: 'more-expensive', listPrice: m.listPriceTl, overPct: overPct.toFixed(1) }; }
      const requiredDiscount = ((m.listPriceTl - bizTl) / m.listPriceTl) * 100;
      return { name: m.competitorName, status: 'need-discount', listPrice: m.listPriceTl, requiredDiscount: requiredDiscount.toFixed(1) };
    });
  };

  const calcPlUnitPrice = (plItem: PriceListItem, discount: number, currency: string): number => {
    let base = 0;
    const usdTry = offerParams.usdTry || 40; const eurTry = offerParams.eurTry || 45;
    if (currency === 'TRY') base = plItem.priceTl || (plItem.priceUsd ? plItem.priceUsd * usdTry : 0);
    else if (currency === 'USD') base = plItem.priceUsd || (plItem.priceTl ? plItem.priceTl / usdTry : 0);
    else if (currency === 'EUR') { const usdBase = plItem.priceUsd || (plItem.priceTl ? plItem.priceTl / usdTry : 0); base = usdBase / (eurTry / usdTry); }
    return parseFloat((base * (1 - discount / 100)).toFixed(3));
  };

  const addedPlIds = plItems.map(i => i.priceListId);

  const addSelectedPlItems = () => {
    if (selectedPlItems.length === 0) return;
    const newRows: PriceListItemRow[] = [];
    selectedPlItems.forEach(plItem => {
      if (addedPlIds.includes(plItem.id)) return;
      const qty = plItem.unit === 'KG' ? 1000 : 500;
      const unitPrice = calcPlUnitPrice(plItem, globalDiscount, form.currency);
      newRows.push({ priceListId: plItem.id, name: plItem.name, unit: plItem.unit, cableType: plItem.cableType, basePriceTl: plItem.priceTl, basePriceUsd: plItem.priceUsd, discount: globalDiscount, adet: 1, quantity: qty, unitPrice, total: qty * unitPrice });
    });
    setPlItems(prev => [...prev, ...newRows]); setSelectedPlItems([]);
  };

  const updatePlItem = (idx: number, field: keyof PriceListItemRow, value: number) => {
    setPlItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      if (field === 'discount') { const plItem = priceListItems.find(p => p.id === item.priceListId); if (plItem) updated.unitPrice = calcPlUnitPrice(plItem, value, form.currency); }
      updated.total = updated.adet * updated.quantity * updated.unitPrice;
      return updated;
    }));
  };

  const applyGlobalDiscount = () => {
    setPlItems(prev => prev.map(item => {
      const plItem = priceListItems.find(p => p.id === item.priceListId);
      if (!plItem) return item;
      const unitPrice = calcPlUnitPrice(plItem, globalDiscount, form.currency);
      return { ...item, discount: globalDiscount, unitPrice, total: item.adet * item.quantity * unitPrice };
    }));
  };

  const removePlItem = (idx: number) => setPlItems(prev => prev.filter((_, i) => i !== idx));

  const grandTotal = items.reduce((sum, i) => sum + i.total, 0);
  const nakliyeTotal = nakliyeMode === 'amount' ? nakliyeAmount : 0;
  const grandTotalWithNakliye = grandTotal + nakliyeTotal;
  const totalKg = items.reduce((sum, item) => sum + item.adet * item.quantity * (item.unit === 'KG' ? 1 : (item.unitWeight || 0)), 0);
  const totalTon = totalKg / 1000;
  const tirCount = Math.ceil(totalTon / 20);
  const plGrandTotal = plItems.reduce((sum, i) => sum + i.total, 0);

  const handleSave = async () => {
    const hasItems = tab === 0 ? items.length > 0 : plItems.length > 0;
    if (!form.customerId || !hasItems) { setError('Müşteri seçiniz ve en az bir ürün ekleyiniz'); return; }
    setSaving(true);
    try {
      const offerItems = tab === 0
          ? items.map(i => ({ productId: i.productId, quantity: i.adet * i.quantity, pcs: i.adet, unitPrice: i.unitPrice, profitMargin: i.profitMargin / 100 }))
          : plItems.map(i => ({ productId: undefined, productName: i.name, quantity: i.adet * i.quantity, unitPrice: i.unitPrice, profitMargin: -(i.discount / 100) }));
      const offer = await createOffer({
        customerId: parseInt(form.customerId),
        contactId: selectedContactId ? parseInt(selectedContactId) : undefined,
        deliveryLocation: form.deliveryLocation,
        downPaymentRate: form.downPaymentRate / 100, paymentDays: form.paymentDays,
        currency: form.currency, notes: form.notes || undefined, language: form.language,
        fromPriceList: tab === 1,
        shippingAmount: nakliyeMode === 'amount' && nakliyeAmount > 0 ? nakliyeAmount : undefined,
        paymentTerms: termsForm.paymentTerms || undefined, deliveryTime: termsForm.deliveryTime || undefined,
        deliveryPlace: termsForm.deliveryPlace || undefined, optionTerms: termsForm.optionTerms || undefined,
        snapshotLmeAl: offerParamsRef.current.lmeAl,
        snapshotAlPrim: marketParams?.alPrim,
        snapshotUsdTry: offerParamsRef.current.usdTry,
        snapshotEurTry: offerParamsRef.current.eurTry,
        snapshotFinans: offerParamsRef.current.finans,
        snapshotNakliye: offerParamsRef.current.nakliye,
        snapshotDiger: offerParamsRef.current.diger,
        snapshotVergiKad: offerParamsRef.current.vergiKad,
        items: offerItems,
      });
      navigate(`/offers/${offer.id}`);
    } catch { setError('Teklif kaydedilirken hata oluştu'); }
    finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  const typeColor: Record<string, string> = { NAYY: BRAND, NA2XY: '#0E7490', NA2XH: '#7C3AED', AER: '#B45309', AAC: '#16A34A', ACSR: '#DC2626' };
  const vergiLabel = (v: number) => v === 0 ? 'Vergisiz %0' : v === 2 ? 'Vergili %7.5' : `Vergi ${v}`;
  const currencySym = form.currency === 'TRY' ? '₺' : form.currency === 'EUR' ? '€' : '$';

  // ─── Rakip chip helper ─────────────────────────────────────────────────────
  const CompChip: React.FC<{ data: any; compName: string }> = ({ data, compName }) => {
    if (!data) return <Chip size="small" label={`${compName}: listede yok`} sx={{ bgcolor: '#f4f5f7', color: '#9ca3af', fontSize: '11px', height: 20, textDecoration: 'line-through' }} />;
    if (data.status === 'more-expensive') return <Chip size="small" label={`${compName}: ${data.listPrice.toFixed(2)} TL · ⚠ biz %${data.overPct} pahalıyız`} sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: '11px', height: 20 }} />;
    if (data.status === 'need-discount') {
      const disc = parseFloat(data.requiredDiscount || '0');
      if (disc < 0.5) return <Chip size="small" label={`${compName}: ${data.listPrice.toFixed(2)} TL · ≈ aynı`} sx={{ bgcolor: '#e0e7ff', color: '#3730a3', fontWeight: 700, fontSize: '11px', height: 20 }} />;
      return <Chip size="small" label={`${compName}: ${data.listPrice.toFixed(2)} TL · ✓ %${data.requiredDiscount} iskonto eşitler`} sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700, fontSize: '11px', height: 20 }} />;
    }
    return null;
  };

  return (
      <Box sx={{ p: 3, bgcolor: '#f5f6fa', minHeight: '100vh' }}>

        {/* ── Header ── */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button startIcon={<ArrowBack sx={{ fontSize: 16 }} />} onClick={() => navigate('/offers')}
                    sx={{ color: '#8a92a6', textTransform: 'none', fontSize: '13px', fontWeight: 500, borderRadius: '8px', minWidth: 0,
                      '&:hover': { bgcolor: '#f0f1f5', color: '#1a2535' } }}>
              Geri
            </Button>
            <Box>
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#1a2535', lineHeight: 1.2 }}>Yeni Teklif</Typography>
              <Typography sx={{ fontSize: '12px', color: '#8a92a6' }}>LME bazlı veya fiyat listesinden</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => navigate('/offers')}
                    sx={{ borderRadius: '8px', color: '#8a92a6', textTransform: 'none', border: `1px solid ${BORDER}`, px: 2 }}>
              İptal
            </Button>
            <Button variant="contained" startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <Save sx={{ fontSize: 16 }} />}
                    onClick={handleSave} disabled={saving}
                    sx={{ bgcolor: ACCENT, borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 2.5, boxShadow: 'none', '&:hover': { bgcolor: '#a93226', boxShadow: 'none' } }}>
              Kaydet
            </Button>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* ── Müşteri & Teklif Bilgileri ── */}
        <Paper elevation={0} sx={{ p: 3, mb: 2, border: `1px solid ${BORDER}`, borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,.05)' }}>
          <SectionTitle>Müşteri & Teklif Bilgileri</SectionTitle>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField select label="Müşteri *" value={form.customerId}
                       onChange={e => setForm({ ...form, customerId: e.target.value })}
                       size="small" sx={{ minWidth: 250, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
              {customers.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.companyName}</MenuItem>)}
            </TextField>
            {form.customerId && (
                <TextField select label="İlgili Kişi" value={selectedContactId}
                           onChange={e => setSelectedContactId(e.target.value)}
                           size="small" sx={{ minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                           helperText={contacts.length === 0 ? 'Müşteriye henüz kişi eklenmemiş' : ''}>
                  {contacts.map(ct => <MenuItem key={ct.id} value={String(ct.id)}>{ct.name}{ct.title ? ` — ${ct.title}` : ''}</MenuItem>)}
                </TextField>
            )}
            <TextField select label="Para Birimi" value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} size="small" sx={{ minWidth: 120, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
              <MenuItem value="USD">🇺🇸 USD</MenuItem>
              <MenuItem value="EUR">🇪🇺 EUR</MenuItem>
              <MenuItem value="TRY">🇹🇷 TRY</MenuItem>
            </TextField>
            <TextField select label="Teslim Yeri" value={form.deliveryLocation} onChange={e => setForm({ ...form, deliveryLocation: e.target.value })} size="small" sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
              {['EXW - İşyeri Teslim','FOB - Gemi Bordası Teslim','CIF - Mal Bedeli, Sigorta ve Navlun Dahil Teslim','DDP - Gümrük Vergileri Ödenmiş Olarak Teslim'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>
            <TextField label="Ön Ödeme (%)" type="number" size="small" value={form.downPaymentRate}
                       onChange={e => setForm({ ...form, downPaymentRate: parseInt(e.target.value) })}
                       InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                       sx={{ minWidth: 130, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
            <TextField label="Vade (Gün)" type="number" size="small" value={form.paymentDays}
                       onChange={e => setForm({ ...form, paymentDays: parseInt(e.target.value) })}
                       InputProps={{ endAdornment: <InputAdornment position="end">gün</InputAdornment> }}
                       sx={{ minWidth: 120, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
            <TextField select label="PDF Dili" size="small" value={form.language} onChange={e => setForm({ ...form, language: e.target.value })} sx={{ minWidth: 120, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
              <MenuItem value="EN">🇬🇧 English</MenuItem>
              <MenuItem value="TR">🇹🇷 Türkçe</MenuItem>
            </TextField>
          </Box>
          <Box sx={{ mt: 2 }}>
            <TextField label="Teklif Notu (PDF'de görünür)" multiline rows={2} fullWidth size="small"
                       value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                       placeholder="Terms & Conditions altında PDF'e eklenir — opsiyonel"
                       inputProps={{ maxLength: 500 }}
                       InputProps={{ endAdornment: <InputAdornment position="end" sx={{ alignSelf: 'flex-end', mb: 1 }}><Typography variant="caption" color="text.secondary">{form.notes.length}/500</Typography></InputAdornment> }}
                       sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
          </Box>

          {/* PDF Koşullar */}
          <Box sx={{ mt: 2.5 }}>
            <SectionTitle color={ACCENT}>PDF Koşullar <Typography component="span" sx={{ fontSize: '11px', fontWeight: 400, color: '#8a92a6', ml: 1 }}>(boş bırakılırsa gösterilmez)</Typography></SectionTitle>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {[
                { label: 'Ödeme Şekli / Payment Terms', key: 'paymentTerms', placeholder: 'örn: %30 peşin, %70 yükleme öncesi...' },
                { label: 'Teslim Süresi / Delivery Time',  key: 'deliveryTime',  placeholder: 'örn: Sipariş onayından itibaren 6-8 hafta...' },
                { label: 'Teslim Yeri / Delivery Place',   key: 'deliveryPlace', placeholder: 'örn: KLC Kablo A.Ş. Fabrika çıkışı...' },
                { label: 'Opsiyon / Option',               key: 'optionTerms',   placeholder: 'örn: Teklif 15 gün geçerlidir...' },
              ].map(f => (
                  <TextField key={f.key} label={f.label} multiline rows={2} size="small"
                             value={(termsForm as any)[f.key]} onChange={e => setTermsForm({ ...termsForm, [f.key]: e.target.value })}
                             placeholder={f.placeholder} sx={{ flex: '1 1 280px', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
              ))}
            </Box>
          </Box>
        </Paper>

        {/* ── Tabs ── */}
        <Paper elevation={0} sx={{ mb: 0, borderBottomLeftRadius: 0, borderBottomRightRadius: 0, border: `1px solid ${BORDER}`, borderBottom: 0, borderRadius: '10px 10px 0 0' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{
            px: 2,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '13px' },
            '& .Mui-selected': { color: ACCENT },
            '& .MuiTabs-indicator': { bgcolor: ACCENT },
          }}>
            <Tab label="⚡ LME Bazlı Fiyatlandırma" />
            <Tab label="📋 Fiyat Listesinden Teklif" />
          </Tabs>
        </Paper>

        {/* ===== TAB 0: LME ===== */}
        {tab === 0 && (
            <Paper elevation={0} sx={{ p: 3, borderTopLeftRadius: 0, borderTopRightRadius: 0, border: `1px solid ${BORDER}`, borderRadius: '0 0 10px 10px', boxShadow: '0 1px 3px rgba(0,0,0,.05)' }}>

              {/* Piyasa Parametreleri */}
              <Box sx={{ mb: 3, border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden' }}>
                <Box sx={{ p: 2, bgcolor: BRAND, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                    {[
                      ['LME AL', `${offerParams.lmeAl} $/t`],
                      ['AL Prim', `${alPrim} $/t`],
                      ['Alaşım', `${alasim.toFixed(0)} $/t`],
                      ['USD/TRY', `₺${offerParams.usdTry}`],
                      ['EUR/TRY', `₺${offerParams.eurTry}`],
                    ].map(([label, val]) => (
                        <Box key={label}>
                          <Typography sx={{ color: 'rgba(255,255,255,.5)', fontSize: '10px', lineHeight: 1, mb: 0.3 }}>{label}</Typography>
                          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '13px' }}>{val}</Typography>
                        </Box>
                    ))}
                    {(offerParams.finans + offerParams.nakliye + offerParams.diger) > 0 && (
                        <Chip label={`Ek: %${offerParams.finans + offerParams.nakliye + offerParams.diger}`} size="small"
                              sx={{ bgcolor: ACCENT, color: '#fff', fontWeight: 700, height: 22, fontSize: '11px' }} />
                    )}
                    <Chip label={vergiLabel(offerParams.vergiKad)} size="small"
                          sx={{ bgcolor: 'rgba(255,255,255,.15)', color: '#fff', height: 22, fontSize: '11px' }} />
                  </Box>
                  <Button size="small" startIcon={<TuneOutlined sx={{ fontSize: 15 }} />}
                          onClick={() => setShowParamsEdit(v => !v)}
                          variant={showParamsEdit ? 'contained' : 'outlined'}
                          sx={{
                            textTransform: 'none', fontSize: '12px', borderRadius: '7px',
                            color: '#fff', borderColor: 'rgba(255,255,255,.4)',
                            '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,.1)' },
                            ...(showParamsEdit ? { bgcolor: ACCENT, borderColor: ACCENT, '&:hover': { bgcolor: '#a93226' } } : {}),
                          }}>
                    {showParamsEdit ? 'Kapat' : 'Düzenle'}
                  </Button>
                </Box>

                <Collapse in={showParamsEdit}>
                  <Box sx={{ p: 2.5, bgcolor: '#f8fbff', borderTop: `1px solid ${BORDER}` }}>
                    <Typography sx={{ fontSize: '11px', color: '#8a92a6', mb: 2 }}>
                      Bu parametreler sadece bu teklif için geçerlidir.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {[
                        { label: 'LME AL', key: 'lmeAl', end: '$/ton', width: 150 },
                        { label: 'USD/TRY', key: 'usdTry', end: '₺', width: 130 },
                        { label: 'EUR/TRY', key: 'eurTry', end: '₺', width: 130 },
                        { label: 'Finans', key: 'finans', end: '%', width: 100 },
                        { label: 'Diğer', key: 'diger', end: '%', width: 100 },
                      ].map(({ label, key, end, width }) => (
                          <TextField key={key} label={label} type="number" size="small"
                                     value={(offerParams as any)[key]}
                                     onChange={e => { const v = { ...offerParams, [key]: parseFloat(e.target.value) || 0 }; offerParamsRef.current = v; setOfferParams(v); }}
                                     slotProps={{ input: { endAdornment: <InputAdornment position="end">{end}</InputAdornment> } }}
                                     sx={{ width, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                      ))}
                      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
                        <ToggleButtonGroup value={nakliyeMode} exclusive size="small" onChange={(_, v) => { if (v) setNakliyeMode(v); }} sx={{ height: 40 }}>
                          <ToggleButton value="percent" sx={{ fontSize: '11px', px: 1.5 }}>%</ToggleButton>
                          <ToggleButton value="amount" sx={{ fontSize: '11px', px: 1.5 }}>{currencySym}</ToggleButton>
                        </ToggleButtonGroup>
                        {nakliyeMode === 'percent'
                            ? <TextField label="Nakliye" type="number" size="small" value={offerParams.nakliye}
                                         onChange={e => { const v = { ...offerParams, nakliye: parseFloat(e.target.value) || 0 }; offerParamsRef.current = v; setOfferParams(v); }}
                                         slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
                                         sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                            : <TextField label="Nakliye" type="number" size="small" value={nakliyeAmount}
                                         onChange={e => setNakliyeAmount(parseFloat(e.target.value) || 0)}
                                         slotProps={{ input: { endAdornment: <InputAdornment position="end">{currencySym}</InputAdornment> } }}
                                         sx={{ width: 130, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                        }
                      </Box>
                      <TextField select label="Vergi" size="small" value={offerParams.vergiKad}
                                 onChange={e => { const v = { ...offerParams, vergiKad: parseInt(e.target.value) }; offerParamsRef.current = v; setOfferParams(v); }}
                                 sx={{ width: 150, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                        <MenuItem value={0}>Vergisiz %0</MenuItem>
                        <MenuItem value={2}>Vergili %7.5</MenuItem>
                      </TextField>
                      <Button variant="contained" startIcon={<Calculate />} size="small"
                              onClick={() => recalcAll()} disabled={items.length === 0}
                              sx={{ height: 40, alignSelf: 'center', bgcolor: BRAND, borderRadius: '8px', textTransform: 'none', boxShadow: 'none', '&:hover': { bgcolor: '#0f2a52', boxShadow: 'none' } }}>
                        Fiyatları Güncelle
                      </Button>
                    </Box>
                  </Box>
                </Collapse>
              </Box>

              {/* Ürün seçimi */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <SectionTitle>Ürünler</SectionTitle>
                  {items.length > 0 && <Chip label={`${items.length} ürün`} size="small" sx={{ bgcolor: BRAND, color: '#fff', fontWeight: 700, height: 20, fontSize: '11px' }} />}
                  {totalTon > 0 && <Chip label={`${totalTon.toFixed(2)} ton · ~${tirCount} TIR`} size="small" sx={{ bgcolor: '#fff8e1', color: '#b45309', fontWeight: 700, fontSize: '11px', height: 20 }} />}
                </Box>
                {items.length > 0 && !showParamsEdit && (
                    <Button variant="outlined" startIcon={<Calculate sx={{ fontSize: 15 }} />} onClick={() => recalcAll()} size="small"
                            sx={{ borderRadius: '7px', textTransform: 'none', borderColor: BRAND, color: BRAND, fontSize: '12px' }}>
                      Fiyatları Güncelle
                    </Button>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
                <Autocomplete multiple disableCloseOnSelect options={products} value={selectedProducts}
                              onChange={(_, v) => setSelectedProducts(v)} getOptionLabel={p => `${p.name} (${p.cableFamily})`}
                              isOptionEqualToValue={(opt, val) => opt.id === val.id}
                              renderInput={params => <TextField {...params} label="Ürün Ara / Seç" size="small" placeholder={selectedProducts.length === 0 ? 'İsim veya kablo ailesi yazın...' : ''} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />}
                              renderOption={(props, option, { selected }) => (
                                  <li {...props} key={option.id} style={{ backgroundColor: selected ? '#e8f4ff' : undefined }}>
                                    <Checkbox icon={<CheckBoxOutlineBlank fontSize="small" />} checkedIcon={<CheckBox fontSize="small" />} checked={selected} sx={{ mr: 1, p: 0.5 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                                      <Box>
                                        <Box sx={{ fontWeight: 600, fontSize: '13px' }}>{option.name}</Box>
                                        <Box sx={{ fontSize: '11px', color: '#8a92a6' }}>{option.cableFamily} · {option.unit}</Box>
                                      </Box>
                                      <Box sx={{ textAlign: 'right', fontSize: '12px', color: '#16a34a', fontWeight: 700, flexShrink: 0 }}>
                                        {calcAutoPrice(option, form.currency).toFixed(3)} {form.currency}
                                      </Box>
                                    </Box>
                                  </li>
                              )}
                              renderTags={(value, getTagProps) => value.map((option, index) => {
                                const tp = getTagProps({ index });
                                return <Chip {...tp} key={option.id} label={option.name} size="small" sx={{ bgcolor: BRAND, color: '#fff', fontSize: '11px', height: 20 }} />;
                              })}
                              filterOptions={(options, { inputValue }) => options.filter(o => o.name.toLowerCase().includes(inputValue.toLowerCase()) || o.cableFamily.toLowerCase().includes(inputValue.toLowerCase()))}
                              sx={{ flex: 1, minWidth: 380 }} noOptionsText="Ürün bulunamadı"
                              slotProps={{ listbox: { style: { maxHeight: 320 } } }} />
                <Button variant="contained" startIcon={<Add />} onClick={addItems} disabled={selectedProducts.length === 0}
                        sx={{ mt: 0.5, minWidth: 140, height: 40, bgcolor: BRAND, borderRadius: '8px', textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { bgcolor: '#0f2a52', boxShadow: 'none' } }}>
                  {selectedProducts.length > 0 ? `${selectedProducts.length} Ürün Ekle` : 'Ekle'}
                </Button>
              </Box>

              {selectedProducts.length > 0 && (
                  <Box sx={{ mb: 2, px: 1.5, py: 1, bgcolor: '#eff6ff', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: BRAND }}>{selectedProducts.length} ürün seçildi —</Typography>
                    {selectedProducts.slice(0, 6).map(p => <Chip key={p.id} label={p.name} size="small" sx={{ bgcolor: BRAND, color: '#fff', fontSize: '10px', height: 18 }} />)}
                    {selectedProducts.length > 6 && <Typography sx={{ fontSize: '11px', color: '#8a92a6' }}>+{selectedProducts.length - 6} daha</Typography>}
                  </Box>
              )}

              {/* LME Ürün tablosu */}
              <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TH minWidth={140}>Ürün</TH>
                      <TH width={60}>Birim</TH>
                      <TH width={90} align="right">Maliyet</TH>
                      <TH width={80} align="center">Adet</TH>
                      <TH width={110} align="center">Miktar</TH>
                      <TH width={110} align="center">Kâr (%)</TH>
                      <TH width={36} />
                      <TH width={130} align="right">Birim Fiyat ({form.currency})</TH>
                      <TH width={120} align="right">Toplam</TH>
                      <TH width={40} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={{ py: 6, color: '#8a92a6', fontSize: '13px' }}>
                            Ürün seçip "Ekle" butonuna basın
                          </TableCell>
                        </TableRow>
                    ) : items.map((item, idx) => (
                        <React.Fragment key={`${item.productId}-${idx}`}>
                          <TableRow hover sx={{ bgcolor: '#fff', '&:hover': { bgcolor: '#f8faff' }, transition: 'background .1s',
                            '& > td': { borderBottom: 'none', pt: 1.2 },
                            '& > td:first-of-type': { borderLeft: `3px solid ${BRAND}` },
                          }}>
                            <TableCell><Typography sx={{ fontWeight: 600, fontSize: '13px', color: BRAND }}>{item.productName}</Typography></TableCell>
                            <TableCell><Chip label={item.unit} size="small" variant="outlined" sx={{ fontSize: '11px', height: 20, borderRadius: '6px' }} /></TableCell>
                            <TableCell align="right"><Typography sx={{ fontSize: '12px', color: '#8a92a6' }}>{item.costPrice.toFixed(3)}</Typography></TableCell>
                            <TableCell align="center">
                              <TextField type="number" value={item.adet} size="small" onChange={e => { const v = parseInt(e.target.value); if (v >= 1) updateItem(idx, 'adet', v); }} inputProps={{ min: 1 }} sx={{ width: 70, '& .MuiOutlinedInput-root': { borderRadius: '6px' } }} />
                            </TableCell>
                            <TableCell align="center">
                              <TextField type="number" value={item.quantity} size="small" onChange={e => updateItem(idx, 'quantity', parseFloat(e.target.value))} sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: '6px' } }} />
                            </TableCell>
                            <TableCell align="right" sx={{ pl: 1, pr: 0 }}>
                              <TextField type="number" value={item.profitMargin} size="small"
                                         onChange={e => { const v = parseFloat(e.target.value); if (v >= 0) updateItem(idx, 'profitMargin', v); }}
                                         inputProps={{ min: 0, step: 0.5 }}
                                         slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
                                         sx={{ width: 85, '& .MuiOutlinedInput-root': { borderRadius: '6px' } }} />
                            </TableCell>
                            <TableCell align="left" sx={{ pl: 0.5, pr: 0 }}>
                              {idx === 0 && items.length > 1 && (
                                  <Tooltip title="Bu kâr oranını tüm ürünlere uygula">
                                    <IconButton size="small"
                                                onClick={() => {
                                                  const margin = item.profitMargin;
                                                  setItems(prev => prev.map((it, i) => {
                                                    if (i === 0) return it;
                                                    const unitPrice = parseFloat((it.autoPrice * (1 + margin / 100)).toFixed(3));
                                                    return { ...it, profitMargin: margin, unitPrice, total: it.adet * it.quantity * unitPrice };
                                                  }));
                                                }}
                                                sx={{ bgcolor: BRAND, color: '#fff', width: 24, height: 24, borderRadius: '5px', '&:hover': { bgcolor: ACCENT } }}>
                                      <Typography sx={{ fontSize: '10px', fontWeight: 700, lineHeight: 1 }}>↓↓</Typography>
                                    </IconButton>
                                  </Tooltip>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <TextField type="number" value={item.unitPrice} size="small" onChange={e => updateItem(idx, 'unitPrice', parseFloat(e.target.value))} sx={{ width: 120, '& .MuiOutlinedInput-root': { borderRadius: '6px' } }} />
                            </TableCell>
                            <TableCell align="right">
                              <Typography sx={{ fontWeight: 700, color: BRAND, fontSize: '13px' }}>
                                {item.total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <IconButton size="small" onClick={() => removeItem(idx)}
                                          style={{ width: 26, height: 26, borderRadius: 5, border: `1px solid ${BORDER}`, background: '#fff', color: '#9ca3af' }}
                                          sx={{ '&:hover': { background: '#fff0f0 !important', color: ACCENT + ' !important', borderColor: '#fca5a5 !important' } }}>
                                <Delete sx={{ fontSize: 13 }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>

                          {/* Rakip analizi satırı */}
                          {(() => {
                            if (!allCompetitors.length) return null;
                            const comparison = getCompetitorComparison(item);
                            return (
                                <TableRow sx={{ bgcolor: '#f8fafc', '& > td': { borderLeft: `3px solid ${BRAND}`, borderBottom: `1px solid ${BORDER}`, pt: 0.5, pb: 1 } }}>
                                  <TableCell colSpan={10} sx={{ py: 0.8, px: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                      <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#8a92a6', letterSpacing: '0.06em', textTransform: 'uppercase', flexShrink: 0 }}>Rakip:</Typography>
                                      {allCompetitors.map(compName => {
                                        const data: any = comparison?.find((c: any) => c.name === compName);
                                        return <CompChip key={compName} data={data} compName={compName} />;
                                      })}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                            );
                          })()}
                          <TableRow sx={{ height: 6, '& td': { border: 'none', p: 0 } }}><TableCell colSpan={10} /></TableRow>
                        </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              {items.length > 0 && (
                  <>
                    <Divider sx={{ my: 2.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                      <Box sx={{ p: 2, bgcolor: '#fffbeb', borderRadius: '10px', border: '1px solid #fde68a' }}>
                        <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>Toplam Ağırlık</Typography>
                        <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#b45309', lineHeight: 1 }}>{totalTon.toFixed(3)} ton</Typography>
                        <Typography sx={{ fontSize: '11px', color: '#b45309' }}>≈ {tirCount} TIR (20t/TIR)</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        {nakliyeMode === 'amount' && nakliyeAmount > 0 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography sx={{ fontSize: '12px', color: '#8a92a6' }}>Ürünler Toplamı</Typography>
                              <Typography sx={{ fontWeight: 700 }}>{grandTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {form.currency}</Typography>
                              <Typography sx={{ fontSize: '12px', color: '#8a92a6', mt: 0.5 }}>Nakliye</Typography>
                              <Typography sx={{ fontWeight: 700, color: '#b45309' }}>+ {nakliyeAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {form.currency}</Typography>
                              <Divider sx={{ my: 0.75 }} />
                            </Box>
                        )}
                        <Box sx={{ px: 3, py: 2, bgcolor: BRAND, borderRadius: '10px', textAlign: 'right' }}>
                          <Typography sx={{ color: 'rgba(255,255,255,.5)', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>Genel Toplam</Typography>
                          <Typography sx={{ fontSize: 28, fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                            {grandTotalWithNakliye.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {form.currency}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </>
              )}
            </Paper>
        )}

        {/* ===== TAB 1: FİYAT LİSTESİ ===== */}
        {tab === 1 && (
            <Paper elevation={0} sx={{ p: 3, borderTopLeftRadius: 0, borderTopRightRadius: 0, border: `1px solid ${BORDER}`, borderRadius: '0 0 10px 10px', boxShadow: '0 1px 3px rgba(0,0,0,.05)' }}>
              <Box sx={{ p: 2, mb: 3, bgcolor: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a', display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#b45309' }}>Toplu İndirim:</Typography>
                <TextField type="number" size="small" label="İndirim %" value={globalDiscount}
                           onChange={e => setGlobalDiscount(parseFloat(e.target.value) || 0)}
                           inputProps={{ min: 0, max: 100, step: 0.5 }}
                           slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
                           sx={{ width: 120, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                <Button variant="outlined" size="small" onClick={applyGlobalDiscount} disabled={plItems.length === 0}
                        sx={{ borderRadius: '7px', textTransform: 'none', borderColor: '#b45309', color: '#b45309', fontSize: '12px' }}>
                  Eklenenlere Uygula
                </Button>
                <Typography sx={{ fontSize: '11px', color: '#8a92a6' }}>Her ürünü ayrı ayrı da düzenleyebilirsiniz</Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
                <Autocomplete multiple disableCloseOnSelect options={priceListItems} value={selectedPlItems}
                              onChange={(_, v) => setSelectedPlItems(v)} getOptionLabel={p => p.name}
                              isOptionEqualToValue={(opt, val) => opt.id === val.id}
                              renderInput={params => <TextField {...params} label="Fiyat Listesinden Seç" size="small" placeholder={selectedPlItems.length === 0 ? 'Ürün adı veya kablo tipi yazın...' : ''} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />}
                              renderOption={(props, option, { selected }) => (
                                  <li {...props} key={option.id} style={{ backgroundColor: selected ? '#e8f4ff' : undefined }}>
                                    <Checkbox icon={<CheckBoxOutlineBlank fontSize="small" />} checkedIcon={<CheckBox fontSize="small" />} checked={selected}
                                              sx={{ mr: 1, p: 0.5, color: typeColor[option.cableType], '&.Mui-checked': { color: typeColor[option.cableType] } }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                                      <Box>
                                        <Box sx={{ fontWeight: 600, fontSize: '13px' }}>{option.name}</Box>
                                        <Box sx={{ fontSize: '11px', color: typeColor[option.cableType] || '#8a92a6', fontWeight: 600 }}>{option.cableType} · {option.unit}</Box>
                                      </Box>
                                      <Box sx={{ textAlign: 'right', fontSize: '12px', flexShrink: 0 }}>
                                        {option.priceTl  && <Box sx={{ color: BRAND,     fontWeight: 700 }}>₺{option.priceTl.toFixed(2)}</Box>}
                                        {option.priceUsd && <Box sx={{ color: '#16a34a', fontWeight: 700 }}>${option.priceUsd.toFixed(2)}</Box>}
                                      </Box>
                                    </Box>
                                  </li>
                              )}
                              renderTags={(value, getTagProps) => value.map((option, index) => {
                                const tp = getTagProps({ index });
                                return <Chip {...tp} key={option.id} label={option.name} size="small" sx={{ bgcolor: typeColor[option.cableType] || '#64748b', color: '#fff', fontSize: '11px', height: 20 }} />;
                              })}
                              filterOptions={(options, { inputValue }) => options.filter(o => o.name.toLowerCase().includes(inputValue.toLowerCase()) || o.cableType.toLowerCase().includes(inputValue.toLowerCase()))}
                              sx={{ flex: 1, minWidth: 400 }} noOptionsText="Ürün bulunamadı"
                              slotProps={{ listbox: { style: { maxHeight: 320 } } }} />
                <Button variant="contained" startIcon={<Add />} onClick={addSelectedPlItems} disabled={selectedPlItems.length === 0}
                        sx={{ mt: 0.5, minWidth: 140, height: 40, bgcolor: BRAND, borderRadius: '8px', textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { bgcolor: '#0f2a52', boxShadow: 'none' } }}>
                  {selectedPlItems.length > 0 ? `${selectedPlItems.length} Ürün Ekle` : 'Ekle'}
                </Button>
              </Box>

              {selectedPlItems.length > 0 && (
                  <Box sx={{ mb: 2, px: 1.5, py: 1, bgcolor: '#eff6ff', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: BRAND }}>{selectedPlItems.length} ürün seçildi —</Typography>
                    {selectedPlItems.slice(0, 6).map(p => <Chip key={p.id} label={p.name} size="small" sx={{ bgcolor: typeColor[p.cableType] || '#64748b', color: '#fff', fontSize: '10px', height: 18 }} />)}
                    {selectedPlItems.length > 6 && <Typography sx={{ fontSize: '11px', color: '#8a92a6' }}>+{selectedPlItems.length - 6} daha</Typography>}
                  </Box>
              )}

              {/* PL Tablo */}
              <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TH minWidth={160}>Ürün</TH>
                      <TH width={90}>Tip</TH>
                      <TH width={60}>Birim</TH>
                      <TH width={130} align="right">Liste Fiyatı</TH>
                      <TH width={80} align="center">Adet</TH>
                      <TH width={100} align="center">Miktar</TH>
                      <TH width={110} align="center">İndirim (%)</TH>
                      <TH width={140} align="right">Birim Fiyat ({form.currency})</TH>
                      <TH width={120} align="right">Toplam</TH>
                      <TH width={40} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {plItems.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={{ py: 6, color: '#8a92a6', fontSize: '13px' }}>
                            Fiyat listesinden ürün seçip "Ekle" butonuna basın
                          </TableCell>
                        </TableRow>
                    ) : plItems.map((item, idx) => (
                        <React.Fragment key={item.priceListId}>
                          <TableRow hover sx={{ bgcolor: '#fff', '&:hover': { bgcolor: '#f8faff' }, transition: 'background .1s',
                            '& > td': { borderBottom: 'none', pt: 1.2 },
                            '& > td:first-of-type': { borderLeft: `3px solid ${BRAND}` },
                          }}>
                            <TableCell><Typography sx={{ fontWeight: 600, fontSize: '13px', color: BRAND }}>{item.name}</Typography></TableCell>
                            <TableCell><Chip label={item.cableType} size="small" sx={{ bgcolor: typeColor[item.cableType] || '#64748b', color: '#fff', fontWeight: 700, fontSize: '10px', height: 20, borderRadius: '5px' }} /></TableCell>
                            <TableCell><Typography sx={{ fontSize: '12px', color: '#8a92a6' }}>{item.unit}</Typography></TableCell>
                            <TableCell align="right">
                              {item.basePriceTl  && <Typography sx={{ fontSize: '11px', color: BRAND, fontWeight: 600, lineHeight: 1.4 }}>₺{item.basePriceTl.toFixed(2)}</Typography>}
                              {item.basePriceUsd && <Typography sx={{ fontSize: '11px', color: '#16a34a', fontWeight: 600, lineHeight: 1.4 }}>${item.basePriceUsd.toFixed(2)}</Typography>}
                            </TableCell>
                            <TableCell align="center">
                              <TextField type="number" value={item.adet} size="small" onChange={e => { const v = parseInt(e.target.value); if (v >= 1) updatePlItem(idx, 'adet', v); }} inputProps={{ min: 1 }} sx={{ width: 70, '& .MuiOutlinedInput-root': { borderRadius: '6px' } }} />
                            </TableCell>
                            <TableCell align="center">
                              <TextField type="number" value={item.quantity} size="small" onChange={e => updatePlItem(idx, 'quantity', parseFloat(e.target.value))} sx={{ width: 90, '& .MuiOutlinedInput-root': { borderRadius: '6px' } }} />
                            </TableCell>
                            <TableCell align="center">
                              <TextField type="number" value={item.discount} size="small"
                                         onChange={e => { const v = parseFloat(e.target.value); if (v >= 0 && v <= 100) updatePlItem(idx, 'discount', v); }}
                                         inputProps={{ min: 0, max: 100, step: 0.5 }}
                                         slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
                                         sx={{ width: 90, '& .MuiOutlinedInput-root': { borderRadius: '6px' } }} />
                            </TableCell>
                            <TableCell align="right"><Typography sx={{ fontWeight: 700, color: '#16a34a', fontSize: '13px' }}>{currencySym}{item.unitPrice.toFixed(3)}</Typography></TableCell>
                            <TableCell align="right"><Typography sx={{ fontWeight: 700, color: BRAND, fontSize: '13px' }}>{item.total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</Typography></TableCell>
                            <TableCell align="center">
                              <IconButton size="small" onClick={() => removePlItem(idx)}
                                          style={{ width: 26, height: 26, borderRadius: 5, border: `1px solid ${BORDER}`, background: '#fff', color: '#9ca3af' }}
                                          sx={{ '&:hover': { background: '#fff0f0 !important', color: ACCENT + ' !important', borderColor: '#fca5a5 !important' } }}>
                                <Delete sx={{ fontSize: 13 }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>

                          {/* PL Rakip analizi */}
                          {(() => {
                            if (!allCompetitors.length) return null;
                            const comparison = getPlCompetitorComparison(item);
                            return (
                                <TableRow sx={{ bgcolor: '#f8fafc', '& > td': { borderLeft: `3px solid ${BRAND}`, borderBottom: `1px solid ${BORDER}`, pt: 0.5, pb: 1 } }}>
                                  <TableCell colSpan={10} sx={{ py: 0.8, px: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                      <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#8a92a6', letterSpacing: '0.06em', textTransform: 'uppercase', flexShrink: 0 }}>Rakip:</Typography>
                                      {allCompetitors.map(compName => {
                                        const data: any = comparison?.find((c: any) => c.name === compName);
                                        return <CompChip key={compName} data={data} compName={compName} />;
                                      })}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                            );
                          })()}
                          <TableRow sx={{ height: 6, '& td': { border: 'none', p: 0 } }}><TableCell colSpan={10} /></TableRow>
                        </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              {plItems.length > 0 && (
                  <>
                    <Divider sx={{ my: 2.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Box sx={{ px: 3, py: 2, bgcolor: BRAND, borderRadius: '10px', textAlign: 'right' }}>
                        <Typography sx={{ color: 'rgba(255,255,255,.5)', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>Genel Toplam</Typography>
                        <Typography sx={{ fontSize: 28, fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                          {plGrandTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {form.currency}
                        </Typography>
                      </Box>
                    </Box>
                  </>
              )}
            </Paper>
        )}
      </Box>
  );
};

export default NewOfferPage;
